import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { getBookingHistory, generateInvoice } from "../services/flightBookingAPI";
import { getAirlineLogo } from "../utils/airlineImages";
import CancelBookingDrawer from "../components/CancelBookingDrawer";
import ContactSupportModal from "../components/bookingFlight/ContactSupportModal";

const ORCHESTRATOR_URL =
  process.env.NODE_ENV === "development"
    ? "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator/development"
    : "https://s4nvnzwoc8.execute-api.ap-south-1.amazonaws.com/default/tbo_api_orchesterator";

const ITEMS_PER_PAGE = 80;

// ── Status badge ─────────────────────────────────────────────────────────────
const getStatusBadge = (status) => {
  const statusConfig = {
    TICKET_ISSUED:            { bg: "bg-green-100",  text: "text-green-700",  label: "Confirmed" },
    SUCCESS:                  { bg: "bg-green-100",  text: "text-green-700",  label: "Confirmed" },
    PAYMENT_SUCCESS:          { bg: "bg-blue-100",   text: "text-blue-700",   label: "Processing" },
    PAYMENT_PENDING:          { bg: "bg-yellow-100", text: "text-yellow-700", label: "Payment Pending" },
    ORDER_CREATING:           { bg: "bg-yellow-100", text: "text-yellow-700", label: "Creating Order" },
    PAYMENT_FAILED:           { bg: "bg-red-100",    text: "text-red-700",    label: "Payment Failed" },
    TICKET_FAILED:            { bg: "bg-red-100",    text: "text-red-700",    label: "Booking Failed" },
    ORDER_FAILED:             { bg: "bg-red-100",    text: "text-red-700",    label: "Order Failed" },
    CANCELLED:                { bg: "bg-gray-100",   text: "text-gray-700",   label: "Cancelled" },
    CANCELLATION_IN_PROGRESS: { bg: "bg-amber-100",  text: "text-amber-700",  label: "Cancel in Progress" },
    INITIATED:                { bg: "bg-gray-100",   text: "text-gray-600",   label: "Initiated" },
    REFUND_REQUESTED:         { bg: "bg-blue-50",    text: "text-blue-600",   label: "Refund Requested" },
    REFUND_IN_PROGRESS:       { bg: "bg-amber-50",   text: "text-amber-600",  label: "Refund In Progress" },
    REFUND_COMPLETED:         { bg: "bg-green-50",   text: "text-green-600",  label: "Refund Completed" },
    REFUND_FAILED:            { bg: "bg-red-50",     text: "text-red-600",    label: "Refund Failed" },
  };
  const config = statusConfig[status] || { bg: "bg-gray-100", text: "text-gray-600", label: status || "Unknown" };
  return (
    <span className={`px-2.5 py-1 rounded-full text-[12px] font-bold uppercase ${config.bg} ${config.text} tracking-wide whitespace-nowrap`}>
      {config.label}
    </span>
  );
};

// ── Formatters ────────────────────────────────────────────────────────────────
const formatTime = (dateString) => {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  } catch { return ""; }
};

const formatDateShort = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString("en-US", { day: "numeric", month: "short" });
  } catch { return "-"; }
};

// ── Main component ────────────────────────────────────────────────────────────
const BookingHistory = () => {
  const navigate = useNavigate();

  // Flight state
  const [bookings, setBookings]       = useState([]);
  const [isLoading, setIsLoading]     = useState(true);
  const [error, setError]             = useState(null);
  const [lastKey, setLastKey]         = useState(null);
  const [hasMore, setHasMore]         = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Hotel state
  const [hotelBookings, setHotelBookings]       = useState([]);
  const [hotelLoadingMore, setHotelLoadingMore] = useState(false);
  const [hotelLastKey, setHotelLastKey]         = useState(null);
  const [hotelHasMore, setHotelHasMore]         = useState(false);

  // Bus state
  const [busBookings, setBusBookings]       = useState([]);
  const [busLoadingMore, setBusLoadingMore] = useState(false);
  const [busLastKey, setBusLastKey]         = useState(null);
  const [busHasMore, setBusHasMore]         = useState(false);
  const [busLoading, setBusLoading]         = useState(false);

  const [activeTab, setActiveTab] = useState("flights");

  const [showSupportModal, setShowSupportModal]           = useState(false);
  const [supportPrefillData, setSupportPrefillData]       = useState({});
  const [showCancelDrawer, setShowCancelDrawer]           = useState(false);
  const [selectedBookingForCancel, setSelectedBookingForCancel] = useState(null);
  const [selectedRcBookingId, setSelectedRcBookingId]     = useState(null);

  const getUserContact = useCallback(() => {
    const user = auth.currentUser;
    if (user?.email) return user.email;
    if (user?.phoneNumber) return user.phoneNumber;
    return null;
  }, []);

  const openCancelDrawer = (booking) => { setSelectedBookingForCancel(booking); setShowCancelDrawer(true); };

  const openSupportModal = (booking) => {
    const user = auth.currentUser;
    const parse = (data) => {
      if (!data) return {};
      if (typeof data === "object") return data;
      try { const p = JSON.parse(data); return typeof p === "string" ? JSON.parse(p) : p; } catch { return {}; }
    };
    const ticketDetails  = parse(booking.ticket_details);
    const bookingRequest = parse(booking.booking_request);
    const itinerary = ticketDetails?.FlightItinerary || ticketDetails?.Response?.Response?.FlightItinerary ||
      bookingRequest?.FlightItinerary || booking?.FlightItinerary || {};
    let segments = itinerary?.Segments || [];
    if (segments.length > 0 && Array.isArray(segments[0])) segments = segments[0];
    const firstSeg = segments[0] || {};
    const lastSeg  = segments[segments.length - 1] || firstSeg;
    const origin = firstSeg?.Origin?.Airport?.CityName || booking.source_name || "Origin";
    const dest   = lastSeg?.Destination?.Airport?.CityName || booking.destination_name || "Destination";
    const pnr    = booking.pnr || booking.booking_pnr || itinerary?.PNR || booking.op_pnr || "N/A";
    const leadPax = itinerary?.Passenger?.find(p => p.IsLeadPax) || {};
    setSupportPrefillData({
      firstName: leadPax.FirstName || '',
      lastName:  leadPax.LastName  || '',
      email:     user?.email       || leadPax.Email     || '',
      phone:     user?.phoneNumber || leadPax.ContactNo || '',
      message:   `I need help with my booking from ${origin} to ${dest} (PNR: ${pnr}).`
    });
    setSelectedRcBookingId(booking.rc_booking_id);
    setShowSupportModal(true);
  };

  // ── Fetch flights ─────────────────────────────────────────────────────────
  const fetchBookings = useCallback(async (loadMore = false, currentLastKey = null) => {
    const userContact = getUserContact();
    if (!userContact) { setError("Please login to view your booking history"); setIsLoading(false); return; }
    try {
      loadMore ? setLoadingMore(true) : setIsLoading(true);
      setError(null);
      const result = await getBookingHistory(userContact, ITEMS_PER_PAGE, loadMore ? currentLastKey : null);
      if (result.error) setError(result.error);
      loadMore ? setBookings(prev => [...prev, ...(result.bookings || [])]) : setBookings(result.bookings || []);
      setLastKey(result.last_evaluated_key);
      setHasMore(!!result.last_evaluated_key);
    } catch (err) {
      setError(err.message || "Failed to load booking history");
    } finally {
      setIsLoading(false);
      setLoadingMore(false);
    }
  }, [getUserContact]);

  // ── Fetch hotels ──────────────────────────────────────────────────────────
  const fetchHotelBookings = useCallback(async (loadMore = false, currentLastKey = null) => {
    const userContact = getUserContact();
    if (!userContact) return;
    try {
      if (loadMore) setHotelLoadingMore(true);
      const payload = {
        intent: "hotelhistory",
        flight_test: false,
        From: `webbot_new:${userContact}`,
        EndUserIp: "125.63.76.234",
        limit: ITEMS_PER_PAGE,
        ...(loadMore && currentLastKey ? { last_evaluated_key: currentLastKey } : {}),
      };
      const response = await fetch(ORCHESTRATOR_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const result = await response.json();
      if (result.status !== 200) return;
      const list = result.data?.hotel_bookings || [];
      loadMore ? setHotelBookings(prev => [...prev, ...list]) : setHotelBookings(list);
      setHotelLastKey(result.data?.last_evaluated_key);
      setHotelHasMore(!!result.data?.last_evaluated_key);
    } catch (err) {
      console.error("[HotelBookings] Error:", err);
    } finally {
      setHotelLoadingMore(false);
    }
  }, [getUserContact]);

  // ── Fetch buses ───────────────────────────────────────────────────────────
  const fetchBusBookings = useCallback(async (loadMore = false, currentLastKey = null) => {
    const userContact = getUserContact();
    if (!userContact) return;
    try {
      loadMore ? setBusLoadingMore(true) : setBusLoading(true);
      const payload = {
        intent: "bus_booking_history",
        From: `webbot_new:${userContact}`,
        EndUserIp: "125.63.76.234",
        limit: ITEMS_PER_PAGE,
        ...(loadMore && currentLastKey ? { last_evaluated_key: currentLastKey } : {}),
      };
      const response = await fetch(ORCHESTRATOR_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const result = await response.json();
      if (result.status !== 200) { console.log("[BusBookings] Error:", result.message); return; }
      const list = result.data?.bookings || [];
      loadMore ? setBusBookings(prev => [...prev, ...list]) : setBusBookings(list);
      setBusLastKey(result.data?.last_evaluated_key);
      setBusHasMore(!!result.data?.last_evaluated_key);
    } catch (err) {
      console.error("[BusBookings] Error:", err);
    } finally {
      setBusLoading(false);
      setBusLoadingMore(false);
    }
  }, [getUserContact]);

  // ── Auth-gated initial load ───────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchBookings(false);
        fetchHotelBookings();
        fetchBusBookings();
      } else {
        setError("Please login to view your booking history");
        setIsLoading(false);
      }
    });
    return () => unsubscribe();
  }, [fetchBookings, fetchHotelBookings, fetchBusBookings]);

  useEffect(() => {
    const onBackButtonEvent = (e) => { e.preventDefault(); navigate('/'); };
    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener('popstate', onBackButtonEvent);
    return () => window.removeEventListener('popstate', onBackButtonEvent);
  }, [navigate]);

  const groupedBookings = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const current = [], past = [];
    bookings.forEach(b => {
      if (["Quoted", "QUOTED", "PAYMENT_PENDING", "payment_pending"].includes(b.status)) return;
      new Date(b.journey_date || b.created_at) >= today ? current.push(b) : past.push(b);
    });
    return { current, past };
  }, [bookings]);

  const groupedBusBookings = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const current = [], past = [];
    busBookings.forEach(b => {
      if (["INITIATED", "PAYMENT_PENDING", "ORDER_FAILED"].includes(b.status)) return;
      new Date(b.journey_date || b.created_at) >= today ? current.push(b) : past.push(b);
    });
    return { current, past };
  }, [busBookings]);

  const handleRefresh = () => {
    setLastKey(null);
    fetchBookings(false);
    fetchHotelBookings();
    fetchBusBookings();
  };

  return (
    <div className="min-h-[87vh] bg-white mt-16 pb-12">
      <main className="w-full md:max-w-7xl mx-auto md:border-x border-gray-100 min-h-[87vh]">

        {/* Header */}
        <div className="bg-[#FFF6EC] px-3 md:px-6 py-4 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M16.5 2V5M7.5 2V5M12 2V5" stroke="#FF4E01" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 3.5H11C7.70017 3.5 6.05025 3.5 5.02513 4.52513C4 5.55025 4 7.20017 4 10.5V15C4 18.2998 4 19.9497 5.02513 20.9749C6.05025 22 7.70017 22 11 22H13C16.2998 22 17.9497 22 18.9749 20.9749C20 19.9497 20 18.2998 20 15V10.5C20 7.20017 20 5.55025 18.9749 4.52512C17.9497 3.5 16.2998 3.5 13 3.5Z" stroke="#FF4E01" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 15H12M8 11H16" stroke="#FF4E01" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="text-[#FF4E01] font-semibold text-lg">Booking History</h1>
          <div className="flex-1" />
          <button onClick={handleRefresh} className={`p-2 hover:bg-orange-100 rounded-full transition-colors ${isLoading ? 'animate-spin' : ''}`}>
            <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-3 md:px-6 flex gap-8 bg-white sticky top-16 z-10">
          {[
            { id: "flights", label: `Flights (${bookings.length})` },
            { id: "hotels",  label: `Hotels (${hotelBookings.length})` },
            { id: "buses",   label: `Buses (${busBookings.length})` },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-2 font-bold text-sm border-b-2 transition ${activeTab === tab.id ? "text-orange-500 border-orange-500" : "text-gray-500 border-transparent hover:text-gray-700"}`}
            >{tab.label}</button>
          ))}
        </div>

        {/* ── FLIGHTS tab ───────────────────────────────────────────────────── */}
        {activeTab === "flights" && (
          <div className="flex flex-col">
            {isLoading && !bookings.length ? (
              <SkeletonRows />
            ) : (
              <div className="flex flex-col">
                <SectionHeader title="Current Bookings" />
                <div className="divide-y divide-gray-100 border-b border-gray-100">
                  {groupedBookings.current.length > 0
                    ? groupedBookings.current.map((b, i) => <BookingRow key={b.rc_booking_id || i} booking={b} onHelpClick={() => openSupportModal(b)} onCancelClick={() => openCancelDrawer(b)} />)
                    : <EmptyState msg="No current bookings found." />}
                </div>
                <SectionHeader title="Past Bookings" />
                <div className="divide-y divide-gray-100">
                  {groupedBookings.past.length > 0
                    ? groupedBookings.past.map((b, i) => <BookingRow key={b.rc_booking_id || i} booking={b} onHelpClick={() => openSupportModal(b)} onCancelClick={() => openCancelDrawer(b)} />)
                    : <EmptyState msg="No past bookings found." />}
                </div>
              </div>
            )}
            {hasMore && <LoadMoreButton onClick={() => fetchBookings(true, lastKey)} loading={loadingMore} />}
            {!isLoading && !bookings.length && !error && <NoBookingsEmpty label="Search Flights" to="/chat" />}
          </div>
        )}

        {/* ── HOTELS tab ────────────────────────────────────────────────────── */}
        {activeTab === "hotels" && (
          <div className="flex flex-col">
            <SectionHeader title="Hotel Bookings" />
            <div className="divide-y divide-gray-100 border-b border-gray-100">
              {hotelBookings.length > 0
                ? hotelBookings.map(b => <HotelBookingRow key={b.rc_booking_id} booking={b} />)
                : <EmptyState msg="No hotel bookings found." />}
            </div>
            {hotelHasMore && <LoadMoreButton onClick={() => fetchHotelBookings(true, hotelLastKey)} loading={hotelLoadingMore} />}
            {!hotelLoadingMore && !hotelBookings.length && <NoBookingsEmpty label="Search Hotels" to="/search" />}
          </div>
        )}

        {/* ── BUSES tab ─────────────────────────────────────────────────────── */}
        {activeTab === "buses" && (
          <div className="flex flex-col">
            {busLoading && !busBookings.length ? (
              <SkeletonRows count={4} />
            ) : (
              <div className="flex flex-col">
                <SectionHeader title="Current Bookings" />
                <div className="divide-y divide-gray-100 border-b border-gray-100">
                  {groupedBusBookings.current.length > 0
                    ? groupedBusBookings.current.map(b => <BusBookingRow key={b.rc_booking_id} booking={b} onHelpClick={() => openSupportModal(b)} navigate={navigate} />)
                    : <EmptyState msg="No current bus bookings found." />}
                </div>
                <SectionHeader title="Past Bookings" />
                <div className="divide-y divide-gray-100">
                  {groupedBusBookings.past.length > 0
                    ? groupedBusBookings.past.map(b => <BusBookingRow key={b.rc_booking_id} booking={b} onHelpClick={() => openSupportModal(b)} navigate={navigate} />)
                    : <EmptyState msg="No past bus bookings found." />}
                </div>
              </div>
            )}
            {busHasMore && <LoadMoreButton onClick={() => fetchBusBookings(true, busLastKey)} loading={busLoadingMore} />}
            {!busLoading && !busBookings.length && <NoBookingsEmpty label="Search Buses" to="/" />}
          </div>
        )}

      </main>

      <CancelBookingDrawer
        isOpen={showCancelDrawer}
        onClose={() => setShowCancelDrawer(false)}
        booking={selectedBookingForCancel}
        onSuccess={handleRefresh}
      />
      <ContactSupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        bookingId={selectedRcBookingId}
        prefillData={supportPrefillData}
      />
    </div>
  );
};

// ── Shared layout helpers ─────────────────────────────────────────────────────
const SectionHeader  = ({ title }) => (
  <div className="bg-[#1A2233] px-3 md:px-6 py-2 mt-0">
    <h2 className="text-white font-medium text-sm text-center">{title}</h2>
  </div>
);
const EmptyState     = ({ msg }) => <div className="px-3 md:px-6 py-8 text-center text-gray-400 text-sm italic">{msg}</div>;
const LoadMoreButton = ({ onClick, loading }) => (
  <div className="flex justify-center py-8">
    <button onClick={onClick} disabled={loading} className="px-6 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-900 transition-colors disabled:opacity-50">
      {loading ? "Loading..." : "Load More"}
    </button>
  </div>
);
const NoBookingsEmpty = ({ label, to }) => (
  <div className="flex flex-col items-center justify-center py-20 text-gray-400">
    <p>Ready for your next adventure?</p>
    <Link to={to} className="mt-4 text-orange-500 font-medium">{label} →</Link>
  </div>
);
const SkeletonRows = ({ count = 7 }) => (
  <div className="divide-y divide-gray-100">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="px-3 md:px-6 py-4 flex flex-col md:flex-row items-center gap-4 animate-pulse">
        <div className="flex items-center gap-4 md:w-[25%] w-full">
          <div className="w-11 h-11 bg-gray-200 rounded-lg" />
          <div className="flex flex-col gap-2 flex-1"><div className="h-4 bg-gray-200 rounded w-3/4" /><div className="h-3 bg-gray-200 rounded w-1/2" /></div>
        </div>
        <div className="flex items-center justify-between md:w-[32%] w-full gap-4">
          <div className="flex flex-col gap-2"><div className="h-5 bg-gray-200 rounded w-16" /><div className="h-3 bg-gray-200 rounded w-20" /></div>
          <div className="flex-1 h-3 bg-gray-200 rounded" />
          <div className="flex flex-col gap-2"><div className="h-5 bg-gray-200 rounded w-16" /><div className="h-3 bg-gray-200 rounded w-20" /></div>
        </div>
        <div className="hidden md:flex md:w-[16%] justify-center"><div className="h-6 bg-gray-200 rounded-full w-24" /></div>
        <div className="hidden md:flex md:w-[12%]"><div className="h-6 bg-gray-200 rounded w-20" /></div>
        <div className="w-full md:w-[15%] flex gap-1.5">{[1,2,3,4,5].map(b => <div key={b} className="flex-1 md:w-9 h-8 bg-gray-200 rounded" />)}</div>
      </div>
    ))}
  </div>
);

// ── Bus Booking Row ───────────────────────────────────────────────────────────
const BusBookingRow = ({ booking, onHelpClick, navigate }) => {
  const isConfirmed = booking.status === "TICKET_ISSUED" || booking.status === "SUCCESS";
  const isFailed    = booking.status?.includes("FAILED");

  // Parse cancellation policy
  let cancelPolicy = [];
  try {
    const raw = booking.cancellation_policy;
    if (raw) cancelPolicy = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {}

  const src  = (booking.source_name      || "").toUpperCase();
  const dest = (booking.destination_name || "").toUpperCase();
  const depTime  = booking.boarding_point
    ? (typeof booking.boarding_point === "string" ? JSON.parse(booking.boarding_point) : booking.boarding_point)?.time?.replace(":00 ", " ")
    : "--";

  // Priority: final_total_with_discounts → amount → total_fare
  // total_fare is 0 from ETS for some operators — always use booking-level amount first
  const fareRaw = parseFloat(
    booking.final_total_with_discounts ||
    booking.amount                     ||
    (parseFloat(booking.total_fare) > 0 ? booking.total_fare : null) ||
    0
  );
  const displayFare = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(fareRaw);

  const handleViewBusBooking = async () => {
    try {
      const user        = auth.currentUser;
      const userContact = user?.email || user?.phoneNumber || '';
      const userToken   = user ? await user.getIdToken().catch(() => null) : null;

      const res = await fetch(ORCHESTRATOR_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent:        'get_bus_booking_detail',
          rc_booking_id: booking.rc_booking_id,
          From:          `webbot_new:${userContact}`,
          headers:       userToken ? { Authorization: userToken } : {},
        }),
      });
      const result = await res.json();
      const bookingData = result?.data?.booking || booking;
      navigate('/book-bus/confirmation', { state: { bookingData } });
    } catch (e) {
      console.error('[BusBookingRow] handleViewBusBooking error:', e);
      // Fallback: navigate with whatever we have in the list
      navigate('/book-bus/confirmation', { state: { bookingData: booking } });
    }
  };

  return (
    <div className="px-3 md:px-6 py-4 flex flex-col md:flex-row items-center hover:bg-gray-50 transition-all border-b border-gray-50">
      {/* Bus icon + route */}
      <div className="flex items-center gap-4 md:w-[25%] w-full mb-4 md:mb-0 flex-shrink-0">
        <div className="w-10 h-10 md:w-11 md:h-11 flex-shrink-0 bg-orange-50 rounded-lg border border-orange-100 flex items-center justify-center">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FF4E01" strokeWidth="1.8">
            <path d="M17 20.5V22M7 20.5V22M4 6.78C4 6.14 4 5.82 4.17 5.43c.17-.39.35-.55.71-.87C5.03 3.53 7.36 2 12 2s6.97 1.53 7.12 1.56c.36.32.54.48.71.87.17.39.17.71.17 1.35V14c0 2.83 0 4.24-.88 5.12C18.24 20 16.83 20 14 20H10c-2.83 0-4.24 0-5.12-.88C4 18.24 4 16.83 4 14V6.78z"/>
            <path d="M4 14c0 0 3.73 1 8 1s8-1 8-1"/>
            <path d="M4.5 17.5H6M18 17.5H19.5M11 17.5H13"/>
            <path d="M4 6h16"/>
          </svg>
        </div>
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-gray-900 font-semibold text-base uppercase tracking-tight truncate">{src}</span>
            <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-gray-900 font-semibold text-base uppercase tracking-tight truncate">{dest}</span>
          </div>
          <span className="text-gray-400 text-[12px] font-medium leading-tight mt-1 uppercase tracking-wide truncate">
            {booking.route_schedule_id?.split("_")[0] || "Bus"}
            {booking.op_pnr ? ` • PNR: ${booking.op_pnr}` : ""}
          </span>
        </div>
      </div>

      {/* Journey time */}
      <div className="flex items-center justify-between md:w-[32%] w-full px-4 mb-4 md:mb-0 flex-shrink-0">
        <div className="flex flex-col items-center">
          <span className="text-gray-900 font-bold text-lg tracking-tight">{depTime || "--"}</span>
          <span className="text-gray-400 text-[12px] uppercase font-bold tracking-widest mt-1">Departure</span>
        </div>
        <div className="flex flex-col items-center px-6 flex-1">
          <span className="text-orange-500 text-[11px] font-bold uppercase">Direct</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="text-gray-900 font-bold text-lg tracking-tight">
            {booking.journey_date ? new Date(booking.journey_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "--"}
          </span>
          <span className="text-gray-400 text-[12px] uppercase font-bold tracking-widest mt-1">Journey</span>
        </div>
      </div>

      {/* Status */}
      <div className="hidden md:flex md:w-[16%] justify-center flex-shrink-0">
        {getStatusBadge(booking.status)}
      </div>

      {/* Price */}
      <div className="hidden md:flex flex-col items-end md:w-[12%] pr-6 flex-shrink-0">
        <span className="text-[#1A2233] font-bold text-xl leading-none tracking-tight">{displayFare}</span>
        <span className="text-gray-400 text-[11px] font-medium uppercase tracking-widest mt-1">Total Fare</span>
      </div>

      {/* Mobile status + price */}
      <div className="md:hidden w-full flex items-center justify-between px-4 mb-4">
        {getStatusBadge(booking.status)}
        <div className="flex flex-col items-end">
          <span className="text-[#1A2233] font-bold text-xl leading-none tracking-tight">{displayFare}</span>
          <span className="text-gray-400 text-[11px] font-medium uppercase tracking-widest mt-1">Total Fare</span>
        </div>
      </div>

      {/* Actions */}
      <div className="w-full md:w-[15%] flex md:justify-end flex-shrink-0">
        <div className="flex flex-wrap gap-1.5 w-full md:w-auto md:justify-end">
          {/* View confirmation — fetches full detail via get_bus_booking_detail */}
          <ActionButton
            icon={<EyeIcon />}
            disabled={!isConfirmed}
            onClick={handleViewBusBooking}
          />
          {/* Help */}
          <ActionButton icon={<HelpIcon />} onClick={onHelpClick} disabled={false} />
        </div>
      </div>
    </div>
  );
};

// ── Flight Booking Row (unchanged from original) ──────────────────────────────
const BookingRow = ({ booking, onHelpClick, onCancelClick }) => {
  const navigate = useNavigate();
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [isGeneratingTicket,  setIsGeneratingTicket]  = useState(false);

  const parseData = (data) => {
    if (!data) return {};
    if (typeof data === "object") return data;
    try { const f = JSON.parse(data); return typeof f === "string" ? JSON.parse(f) : f; } catch { return {}; }
  };

  const bookingRequest = parseData(booking.booking_request);
  const ticketDetails  = parseData(booking.ticket_details);
  const itinerary = ticketDetails?.FlightItinerary || ticketDetails?.Response?.Response?.FlightItinerary ||
    ticketDetails?.Response?.FlightItinerary || bookingRequest?.FlightItinerary ||
    bookingRequest?.Response?.Response?.FlightItinerary || {};

  let segments = itinerary?.Segments || [];
  if (segments.length > 0 && Array.isArray(segments[0])) segments = segments[0];

  const firstSeg = segments[0] || {};
  const lastSeg  = segments[segments.length - 1] || firstSeg;

  const airlineCode = firstSeg?.Airline?.AirlineCode || "AI";
  const airlineName = firstSeg?.Airline?.AirlineName || "Airline";
  const depTime     = firstSeg?.Origin?.DepTime      || booking.journey_date;
  const arrTime     = lastSeg?.Destination?.ArrTime  || booking.arrival_datetime;
  const originCity  = firstSeg?.Origin?.Airport?.CityName       || booking.source_name      || "Origin";
  const destCity    = lastSeg?.Destination?.Airport?.CityName   || booking.destination_name || "Destination";
  const pnr         = booking.pnr || booking.booking_pnr || itinerary?.PNR || "N/A";
  const numStops    = segments.length > 1 ? segments.length - 1 : 0;
  const stopInfo    = numStops === 0 ? "Nonstop" : `${numStops} Stop${numStops > 1 ? 's' : ''}`;

  let finalTotalFare = 0;
  if (booking?.total_final_fare_with_taxes_and_discounts) {
    finalTotalFare = Number(booking.total_final_fare_with_taxes_and_discounts);
  } else {
    const publishedFare = Number(ticketDetails?.FlightItinerary?.Fare?.PublishedFare || 0);
    const promoAmount   = Number(booking?.applied_promo_discount || 0);
    if (publishedFare > 0) {
      const platformFee = Number(booking?.rc_platform_fee || 0);
      const gstRate     = Number(booking?.rc_gst_rate     || 0);
      let effectivePlatformFee = platformFee;
      let remainingDiscount    = promoAmount;
      let displayGst           = (platformFee * gstRate) / 100;
      if (promoAmount > 0 && platformFee > 0) {
        const feeDiscount    = Math.min(platformFee, promoAmount);
        effectivePlatformFee = platformFee - feeDiscount;
        remainingDiscount    = promoAmount - feeDiscount;
        displayGst           = (effectivePlatformFee * gstRate) / 100;
      }
      finalTotalFare = publishedFare + effectivePlatformFee + displayGst - remainingDiscount;
    } else {
      finalTotalFare = Number(booking?.amount || 0) - Number(booking?.applied_promo_discount || 0);
    }
  }

  const displayPrice = new Intl.NumberFormat("en-IN", { style: "currency", currency: booking.currency || "INR", minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(finalTotalFare);

  const isConfirmed       = booking.status === "TICKET_ISSUED" || booking.status === "SUCCESS";
  const isFailed          = booking.status?.includes("FAILED");
  const isCancelInProgress= booking.status === "CANCELLATION_IN_PROGRESS";
  const isCancelled       = booking.status === "CANCELLED";

  const handleDownloadTicket = async () => {
    if (isGeneratingTicket) return;
    try {
      setIsGeneratingTicket(true);
      const user = auth.currentUser;
      const userIdentifier = (user?.email || user?.phoneNumber?.replace("+91", "") || booking?.user_id || "anon").trim();
      const response = await generateInvoice({ ticket_details: ticketDetails, platform_fee: booking?.rc_platform_fee || 0, gst_rate: booking?.rc_gst_rate || 0, rc_booking_id: booking?.rc_booking_id, user_id: userIdentifier, document_type: 'ticket' });
      if (response.status === "success" && response.ticket_url) {
        const a = document.createElement('a'); a.href = response.ticket_url; a.download = `ticket_${booking.rc_booking_id}.pdf`; a.target = '_blank'; document.body.appendChild(a); a.click(); document.body.removeChild(a);
      } else { throw new Error(response.message || "Failed to generate ticket"); }
    } catch (err) { console.error(err); alert("Failed to generate ticket."); }
    finally { setIsGeneratingTicket(false); }
  };

  const handleDownloadInvoice = async () => {
    if (isGeneratingInvoice) return;
    try {
      setIsGeneratingInvoice(true);
      const user = auth.currentUser;
      const userIdentifier = (user?.email || user?.phoneNumber?.replace("+91", "") || booking?.user_id || "anon").trim();
      const response = await generateInvoice({ ticket_details: ticketDetails, platform_fee: booking?.rc_platform_fee || 0, gst_rate: booking?.rc_gst_rate || 0, rc_booking_id: booking?.rc_booking_id, user_id: userIdentifier });
      if (response.status === "success" && response.invoice_url) {
        const a = document.createElement('a'); a.href = response.invoice_url; a.download = `invoice_${booking.rc_booking_id}.pdf`; a.target = '_blank'; document.body.appendChild(a); a.click(); document.body.removeChild(a);
      } else { throw new Error(response.message || "Failed to generate invoice"); }
    } catch (err) { console.error(err); alert("Failed to generate invoice."); }
    finally { setIsGeneratingInvoice(false); }
  };

  return (
    <div className="px-3 md:px-6 py-4 flex flex-col md:flex-row items-center hover:bg-gray-50 transition-all border-b border-gray-50 group relative">
      <div className="flex items-center gap-4 md:w-[25%] w-full mb-4 md:mb-0 flex-shrink-0">
        <div className="w-10 h-10 md:w-11 md:h-11 flex-shrink-0 bg-white rounded-lg border border-gray-100 p-1.5 flex items-center justify-center shadow-sm">
          <img src={getAirlineLogo(airlineCode, airlineName)} alt={airlineName} className="w-full h-full object-contain" onError={(e) => { e.target.src = "/nologo.gif"; }} />
        </div>
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-gray-900 font-semibold text-base uppercase tracking-tight truncate">{originCity}</span>
            <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="text-gray-900 font-semibold text-base uppercase tracking-tight truncate">{destCity}</span>
          </div>
          <span className="text-gray-400 text-[12px] md:text-[13px] font-medium leading-tight mt-1 uppercase tracking-wide truncate">
            {airlineName} • <span className="text-gray-600 font-mono font-bold">PNR: {pnr}</span>
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between md:w-[32%] w-full px-4 mb-4 md:mb-0 flex-shrink-0">
        <div className="flex flex-col items-center">
          <span className="text-gray-900 font-bold text-lg tracking-tight">{formatTime(depTime)}</span>
          <span className="text-gray-400 text-[12px] uppercase font-bold tracking-widest mt-1">Departure</span>
        </div>
        <div className="flex flex-col items-center px-6 flex-1">
          <div className="flex items-center gap-1.5 w-full justify-center opacity-70">
            {segments.map((_, index) => (
              <React.Fragment key={index}>
                {index > 0 && <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className="text-gray-400"><path d="M4.23492 2.73492C4.37222 2.59762 4.58958 2.58889 4.73687 2.70904L4.7652 2.73492L7.7652 5.73492L7.79107 5.76324C7.90323 5.90072 7.90323 6.09939 7.79107 6.23687L7.7652 6.26519L4.7652 9.26519C4.61875 9.41164 4.38137 9.41164 4.23492 9.26519C4.08848 9.11875 4.08848 8.88137 4.23492 8.73492L6.96978 6.00006L4.23492 3.2652L4.20904 3.23687C4.08889 3.08958 4.09762 2.87222 4.23492 2.73492Z" fill="currentColor"/></svg>}
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="text-gray-600"><path d="M1.33325 13.3335H11.9999" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/><path d="M2.55006 8.11057C2.36672 7.97724 2.20006 7.81057 2.00006 7.22724C1.94582 7.08257 1.7408 6.3573 1.56673 5.6939C1.4199 5.1343 1.28906 4.62354 1.35006 4.4939C1.40007 4.36056 1.46672 4.26056 1.68339 4.12723C1.81672 4.04518 2.50006 3.87723 2.63339 3.8439C2.76672 3.81056 2.95005 3.7939 3.10006 3.8439C3.38339 3.8939 3.96672 4.7439 4.11672 4.8439C4.18338 4.91056 4.40006 5.1045 4.65006 5.12723C4.83339 5.1439 5.01672 5.0939 5.21673 5.01056C5.40006 4.93418 9.01674 3.17723 9.35008 3.02723C12.0667 1.8939 14.0401 3.75723 14.3401 4.15723C14.6501 4.5439 14.7167 4.66056 14.6334 4.9939C14.5257 5.3439 14.2334 5.41056 14.0667 5.46056C13.9001 5.51056 11.6001 6.12723 10.7001 6.37723C10.5035 6.44124 10.4075 6.56978 10.3834 6.5939C10.2667 6.76057 9.73674 7.8939 9.58674 8.1439C9.48341 8.41057 9.20008 8.7439 8.83341 8.87724C8.45008 9.01057 7.78341 9.16057 7.63341 9.21057C7.48341 9.26057 7.13341 9.36057 7.01674 9.32724C6.86674 9.2939 6.72341 9.1439 6.79008 8.91057C6.85674 8.67724 6.98341 8.02724 7.00008 7.92724C7.01675 7.82724 7.18341 7.41057 7.00008 7.3939C6.96675 7.3439 6.61673 7.4939 6.10006 7.61057C5.71621 7.71864 5.31421 7.82557 5.03339 7.8939C3.95006 8.21057 3.36335 8.2939 3.23339 8.2939C2.98339 8.2939 2.80006 8.26057 2.55006 8.11057Z" stroke="currentColor"/></svg>
              </React.Fragment>
            ))}
          </div>
          <span className="text-orange-500 text-[11px] font-bold uppercase mt-1">{stopInfo}</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="text-gray-900 font-bold text-lg tracking-tight">{arrTime ? formatTime(arrTime) : "--:--"}</span>
          <span className="text-gray-400 text-[12px] uppercase font-bold tracking-widest mt-1">Arrival</span>
        </div>
      </div>

      <div className="hidden md:flex md:w-[16%] justify-center flex-shrink-0">
        {getStatusBadge((isCancelled || isFailed || isCancelInProgress) ? (booking.refund_status || booking.status) : booking.status)}
      </div>
      <div className="hidden md:flex flex-col items-end md:w-[12%] pr-6 flex-shrink-0">
        <span className="text-[#1A2233] font-bold text-xl leading-none tracking-tight text-right">{displayPrice}</span>
        <span className="text-gray-400 text-[11px] font-medium uppercase tracking-widest mt-1 text-right">Total Fare</span>
      </div>
      <div className="md:hidden w-full flex items-center justify-between px-4 mb-4">
        {getStatusBadge((isCancelled || isFailed || isCancelInProgress) ? (booking.refund_status || booking.status) : booking.status)}
        <div className="flex flex-col items-end">
          <span className="text-[#1A2233] font-bold text-xl leading-none tracking-tight">{displayPrice}</span>
          <span className="text-gray-400 text-[11px] font-medium uppercase tracking-widest mt-1">Total Fare</span>
        </div>
      </div>

      <div className="w-full md:w-[15%] flex md:justify-end flex-shrink-0">
        <div className="flex flex-wrap gap-1.5 w-full md:w-auto md:justify-end">
          <ActionButton icon={<DownloadIcon />} onClick={handleDownloadTicket}  disabled={!isConfirmed} isLoading={isGeneratingTicket} />
          <ActionButton icon={<InvoiceIcon  />} onClick={handleDownloadInvoice} disabled={!isConfirmed} isLoading={isGeneratingInvoice} />
          <ActionButton icon={<EyeIcon      />} onClick={() => navigate(`/booking-detail?rc_booking_id=${booking.rc_booking_id}`)} disabled={!isConfirmed} />
          <ActionButton icon={<XIcon        />} onClick={onCancelClick} disabled={!isConfirmed || isCancelInProgress || isCancelled} />
          <ActionButton icon={<HelpIcon     />} onClick={onHelpClick} disabled={false} />
        </div>
      </div>
    </div>
  );
};

// ── Hotel Booking Row (unchanged) ─────────────────────────────────────────────
const HotelBookingRow = ({ booking }) => {
  const checkIn  = formatDateShort(booking.check_in_date);
  const checkOut = formatDateShort(booking.check_out_date);
  const amount   = booking.amount ? Number(booking.amount).toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "0";
  let statusColor = "bg-gray-100 text-gray-700";
  if (["BOOKING_CONFIRMED", "PAYMENT_SUCCESS"].includes(booking.status)) statusColor = "bg-green-100 text-green-700";
  else if (["BOOKING_FAILED", "PAYMENT_FAILED"].includes(booking.status)) statusColor = "bg-red-100 text-red-700";
  else if (booking.status === "PAYMENT_PENDING") statusColor = "bg-yellow-100 text-yellow-700";

  return (
    <div className="px-3 md:px-6 py-4 flex flex-col md:flex-row items-center hover:bg-gray-50 transition-all border-b border-gray-50">
      <div className="flex-1 md:w-[40%] mb-4 md:mb-0">
        <h3 className="text-sm font-bold text-gray-900">{booking.hotel_name}</h3>
        <p className="text-xs text-gray-500 mt-1">{booking.room_type_name}</p>
        <p className="text-xs text-gray-400 mt-2">Guest: {booking.guest_name}</p>
      </div>
      <div className="hidden md:flex md:w-[20%] text-center flex-col">
        <span className="text-sm text-gray-900 font-bold">{checkIn}</span>
        <span className="text-xs text-gray-500">Check-in</span>
        <span className="text-sm text-gray-900 font-bold mt-2">{checkOut}</span>
        <span className="text-xs text-gray-500">Check-out</span>
      </div>
      <div className="hidden md:flex md:w-[10%] justify-center">
        <span className="text-sm font-bold text-gray-900">{booking.num_rooms} room{booking.num_rooms > 1 ? 's' : ''}</span>
      </div>
      <div className={`flex md:w-[15%] justify-center rounded px-3 py-1 text-xs font-bold ${statusColor}`}>{booking.status}</div>
      <div className="hidden md:flex md:w-[15%] justify-end flex-col items-end">
        <span className="text-lg font-bold text-gray-900">₹{amount}</span>
        <span className="text-xs text-gray-500">{booking.currency}</span>
      </div>
      <div className="md:hidden w-full flex flex-col gap-2 mt-4 pt-4 border-t border-gray-100">
        <div className="flex justify-between"><span className="text-xs text-gray-500">{checkIn} → {checkOut}</span><span className="text-sm font-bold">₹{amount}</span></div>
        <div className="flex justify-between items-center"><span className="text-xs text-gray-500">{booking.num_rooms} room(s)</span><span className={`rounded px-2 py-1 text-xs font-bold ${statusColor}`}>{booking.status}</span></div>
      </div>
    </div>
  );
};

// ── Action button ─────────────────────────────────────────────────────────────
const ActionButton = ({ icon, onClick, primary, disabled, isLoading }) => (
  <button
    onClick={(e) => { e.stopPropagation(); if (onClick) onClick(); }}
    disabled={disabled || isLoading}
    className={`flex-1 md:flex-none md:w-9 h-8 flex items-center justify-center rounded transition-all duration-200
      ${(disabled || isLoading) ? 'opacity-30 cursor-not-allowed border-gray-100' : ''}
      ${primary ? 'bg-[#1A2233] text-white border-[#1A2233] hover:bg-[#2a354a]' : 'text-gray-500 bg-white border-gray-200 hover:bg-[#1A2233] hover:text-white hover:border-[#1A2233]'}`}
    style={{ boxShadow: '0px 0px 0px 1px #D4D4D8, 0px 1px 3px #D4D4D8' }}
  >
    {isLoading
      ? <svg className="animate-spin h-3.5 w-3.5 text-current" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
      : React.cloneElement(icon, { size: 13 })}
  </button>
);

// ── Icons ─────────────────────────────────────────────────────────────────────
const DownloadIcon = ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const InvoiceIcon  = ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>;
const EyeIcon      = ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const XIcon        = ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const HelpIcon     = ({ size = 14 }) => <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path fillRule="evenodd" d="M12 2a7 7 0 0 0-7 7 3 3 0 0 0-3 3v2a3 3 0 0 0 3 3h1a1 1 0 0 0 1-1V9a5 5 0 1 1 10 0v7.083A2.919 2.919 0 0 1 14.083 19H14a2 2 0 0 0-2-2h-1a2 2 0 0 0-2 2v1a2 2 0 0 0 2 2h1a2 2 0 0 0 1.732-1h.351a4.917 4.917 0 0 0 4.83-4H19a3 3 0 0 0 3-3v-2a3 3 0 0 0-3-3 7 7 0 0 0-7-7Zm1.45 3.275a4 4 0 0 0-4.352.976 1 1 0 0 0 1.452 1.376 2.001 2.001 0 0 1 2.836-.067 1 1 0 1 0 1.386-1.442 4 4 0 0 0-1.321-.843Z" clipRule="evenodd"/></svg>;

export default BookingHistory;
